import { Component, ElementRef, afterNextRender, inject, model, viewChild } from '@angular/core';
import { ContactFormErrors, ContactFormStore, ContactFormValue } from './contact-form.store';

type ConsentField = 'tcpaConsent' | 'marketingConsent';

/**
 * TCPA (required) and marketing (optional) consents; projected content renders below the TCPA error. The copy is
 * saved to the store as soon as it renders; the checkboxes only choose which saved copy `consentedTo` includes.
 */
@Component({
  selector: 'app-contact-consents',
  standalone: true,
  template: `
    <label class="consent">
      <input type="checkbox" [checked]="value().tcpaConsent" (change)="update('tcpaConsent', $event)" />
      <span #tcpaText>I agree to the <a [href]="termsUrl">Terms of Service</a> and <a [href]="privacyUrl">Privacy Policy</a> and authorize Kin Insurance, Inc. (“Kin”) to contact me at the email and/or phone number I provided with respect to the inquiry, including using an automated telephone dialing system, SMS texts, and/or prerecorded or artificial intelligence voice system even if my number is on a Do Not Call List. I understand that I can revoke my consent at any time by contacting Kin at {{ supportPhone }} or <a [href]="'mailto:' + supportEmail">{{ supportEmail }}</a>. <strong>(Required)</strong></span>
    </label>
    @if (errors().tcpaConsent) { <p class="field-error consent-error">{{ errors().tcpaConsent }}</p> }
    <ng-content />

    <label class="consent">
      <input type="checkbox" [checked]="value().marketingConsent" (change)="update('marketingConsent', $event)" />
      <span #marketingText>I authorize Kin and its affiliates including Elite Insurance Group, Inc. and Kin Financing, LLC to contact me at the email and/or phone number I provided for marketing purposes including using an automated dialing system, SMS texts, and/or prerecorded or artificial intelligence voice system even if my number is on a Do Not Call List. I understand that I can revoke my consent at any time by contacting Kin at {{ supportPhone }} or <a [href]="'mailto:' + supportEmail">{{ supportEmail }}</a>. <strong>(Optional)</strong></span>
    </label>
  `,
  styles: `
    :host {
      display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; padding: 16px;
      background: var(--kin-neutral-100); border: 1px solid var(--kin-border-subtle); border-radius: var(--kin-radius-lg);
    }
    .consent { display: flex; gap: 10px; align-items: flex-start; cursor: pointer; }
    .consent input { margin-top: 3px; flex-shrink: 0; accent-color: var(--kin-neutral-1000); }
    .consent span { font-size: 12px; line-height: 1.5; color: var(--kin-text-secondary); }
    .consent strong { color: var(--kin-text); font-weight: 700; }
    .field-error { color: var(--kin-red-600); font-size: 12px; font-weight: 500; margin: 0; }
    .consent-error { margin: -4px 0 0 26px; }
  `,
})
export class ContactConsentsComponent {
  readonly value = model.required<ContactFormValue>();
  readonly errors = model<ContactFormErrors>({});

  // Legal and support links
  termsUrl = 'https://kin.com/terms';
  privacyUrl = 'https://kin.com/privacy';
  supportPhone = '(855) 216-7674';
  supportEmail = 'support@kin.com';

  private readonly tcpaText = viewChild.required<ElementRef<HTMLElement>>('tcpaText');
  private readonly marketingText = viewChild.required<ElementRef<HTMLElement>>('marketingText');

  constructor() {
    const store = inject(ContactFormStore);
    // Saved once, right as it renders: the DOM isn't read again, so nothing that edits the page later (e.g. a
    // browser extension) can change the consent that's sent
    afterNextRender(() => store.saveConsentCopy({
      tcpa: this.tcpaText().nativeElement.innerHTML,
      marketing: this.marketingText().nativeElement.innerHTML,
    }));
  }

  protected update(field: ConsentField, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.value.update(v => ({ ...v, [field]: checked }));
    if (this.errors()[field]) this.errors.update(e => ({ ...e, [field]: undefined }));
  }
}
